import { useListingCatalog } from 'app/hooks/use-listing-catalog';
import { Text } from 'app/design/primitives/text';
import React, { type ComponentProps, memo, useEffect, useMemo, useRef } from 'react';
import { Pressable } from 'react-native';
import { Column, Row } from 'app/design/primitives/box';
import { CategoryFilter } from 'app/screens/billboard/all-listings/components/category-filter';
import { TagFilter } from 'app/screens/billboard/all-listings/components/tag-filter';

import { getAddressIcon } from 'app/screens/profile/my-clubs';
import { Switch } from 'app/design/primitives/switch';
import { Image } from 'expo-image';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import type { Group } from 'app/types/group';
import type { Address } from 'app/types/address';
import type { TFunction } from 'i18next';
import type { ServiceCategory } from 'app/types/category';
import { createTheme as createDSTheme } from 'app/shopify';
import { isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
const theme = createDSTheme();

const ListingsFiltersSheetContent = memo(function ListingsFiltersSheetContentComponent({
  t,
  isSmallUI,
  hideGroups,
  addresses,
  groups,
  selectedAddresses,
  selectedGroups,
  selectedCategories,
  selectedTags,
  selectedServiceCategories,
  selectedSubtypes,
  selectedTransactionModes,
  hideFoundRequests,
  dismissedOnly,
  hideOffersWithFee,
  hideCategoryAndTagFilters = false,
  toggleAddress,
  toggleGroup,
  toggleSubtype,
  toggleFoundRequests,
  toggleDismissedRequests,
  toggleOffersWithFee,
  toggleTransactionMode,
  onSave,
  listingType,
  listingSubtypes,
  simplifiedServices,
  loanFeeAvailable,
}: Readonly<{
  t: TFunction<'listings-filters'>;
  isSmallUI: boolean;
  hideGroups: boolean;
  addresses: Address[];
  groups: Group[];
  selectedAddresses: string[];
  selectedGroups: string[];
  selectedCategories: string[];
  selectedTags: string[];
  selectedServiceCategories: string[];
  selectedSubtypes: ListingSubtype[];
  selectedTransactionModes: TransactionMode[];
  hideFoundRequests: boolean;
  dismissedOnly: boolean;
  hideOffersWithFee: boolean;
  hideCategoryAndTagFilters?: boolean;
  toggleAddress: (addressId: string) => void;
  toggleGroup: (groupId: string) => void;
  toggleSubtype: (subtype: ListingSubtype) => void;
  toggleFoundRequests: () => void;
  toggleDismissedRequests: () => void;
  toggleOffersWithFee: () => void;
  toggleTransactionMode: (mode: TransactionMode) => void;
  onSave: (
    newSearchDistance?: number,
    newSearchCenter?: { latitude: number; longitude: number },
    newCategories?: string[],
    newTags?: string[],
    newServiceCategories?: string[],
    newSubtypes?: ListingSubtype[],
    newHideFoundRequests?: boolean,
    newDismissedOnly?: boolean,
    newHideOffersWithFee?: boolean,
    newTransactionModes?: TransactionMode[],
  ) => void;
  listingType: ListingType;
  listingSubtypes: ListingSubtype[];
  simplifiedServices: ServiceCategory['id'] | false;
  loanFeeAvailable: boolean;
}>) {
  const hasInitializedAudienceSelection = useRef(false);
  const selectedSubtypeSet = useMemo(() => new Set(selectedSubtypes), [selectedSubtypes]);
  const { categoriesEnabled, tagsEnabled } = useListingCatalog();
  const hideCategories = hideCategoryAndTagFilters || !categoriesEnabled;
  const hasDonationSubtype = listingSubtypes.includes(ListingSubtype.donation);
  const titleGap = 'spacing.general.x2';
  const addressOrGroupIconSize = 38;
  const showTransactionModeFilters = listingType === ListingType.offer && isWhiteLabel(WhiteLabel.HauteGaspesie);
  const addressOrGroupTextProps: Partial<ComponentProps<typeof Text>> = {
    color: 'text.secondary',
    flex: 1,
    fontWeight: '700',
    minWidth: 0,
    textAlign: 'left',
    fontSize: 14,
  };

  useEffect(() => {
    if (hideGroups) return;

    if (!hasInitializedAudienceSelection.current) {
      hasInitializedAudienceSelection.current = true;
      return;
    }

    if (selectedAddresses.length > 0 || selectedGroups.length > 0) return;
    if (addresses.length === 0 && groups.length === 0) return;

    addresses.forEach((address) => toggleAddress(address.id));
    groups.forEach((group) => toggleGroup(group.id));
  }, [addresses, groups, hideGroups, selectedAddresses.length, selectedGroups.length, toggleAddress, toggleGroup]);

  return (
    <Column gap="spacing.general.x6" width="100%">
      <TransactionModeFilters
        showTransactionModeFilters={showTransactionModeFilters}
        titleGap={titleGap}
        t={t}
        isSmallUI={isSmallUI}
        toggleTransactionMode={toggleTransactionMode}
        selectedTransactionModes={selectedTransactionModes}
      />

      <AudienceFilters
        hideGroups={hideGroups}
        titleGap={titleGap}
        t={t}
        isSmallUI={isSmallUI}
        groups={groups}
        addresses={addresses}
        toggleAddress={toggleAddress}
        addressOrGroupIconSize={addressOrGroupIconSize}
        addressOrGroupTextProps={addressOrGroupTextProps}
        selectedAddresses={selectedAddresses}
        toggleGroup={toggleGroup}
        selectedGroups={selectedGroups}
      />

      {listingType === ListingType.request && (
        <Column gap={titleGap}>
          <Text fontWeight="700" variant="bodyBase">
            {t('titleFoundRequests')}
          </Text>
          <Column
            gap="spacing.general.x4"
            style={{
              borderRadius: theme.borderRadii['radius.lg'],
              borderColor: theme.colors['surface.tertiary-neutral'],
              borderWidth: 1,
              padding: isSmallUI ? theme.spacing['spacing.general.x2'] : theme.spacing['spacing.general.x4'],
              width: '100%',
            }}
          >
            <Row alignItems="center" gap="spacing.general.x4" justifyContent="space-between" minWidth={0}>
              <Pressable onPress={() => toggleFoundRequests()} style={{ flex: 1, minWidth: 0 }}>
                <Text color="text.secondary" flex={1} fontWeight="700" minWidth={0} variant="bodyBase">
                  {t('foundRequestsLabel')}
                </Text>
              </Pressable>

              <Switch onValueChange={() => toggleFoundRequests()} value={!hideFoundRequests} />
            </Row>

            <Row alignItems="center" gap="spacing.general.x4" justifyContent="space-between" minWidth={0}>
              <Pressable onPress={() => toggleDismissedRequests()} style={{ flex: 1, minWidth: 0 }}>
                <Text color="text.secondary" flex={1} fontWeight="700" minWidth={0} variant="bodyBase">
                  {t('dismissedRequestsLabel')}
                </Text>
              </Pressable>

              <Switch onValueChange={() => toggleDismissedRequests()} value={dismissedOnly} />
            </Row>
          </Column>
        </Column>
      )}

      {listingType === ListingType.offer && loanFeeAvailable ? (
        <Text fontWeight="700" variant="bodyBase">
          {t('titleOffersWithFee')}
        </Text>
      ) : null}

      {listingType === ListingType.offer && loanFeeAvailable ? (
        <OffersWithFeeFilter
          isSmallUI={isSmallUI}
          toggleOffersWithFee={toggleOffersWithFee}
          hideOffersWithFee={hideOffersWithFee}
          t={t}
        />
      ) : null}

      {!showTransactionModeFilters && (!hideCategories || listingSubtypes.length > 1) && (
        <Column gap={titleGap}>
          <Text fontWeight="700" variant="bodyBase">
            {t(listingSubtypes.length > 1 ? 'titleSubtypes' : 'titleCategories')}
          </Text>

          {listingSubtypes.map((subtype) => (
            <Column gap="spacing.general.x3" key={subtype}>
              {listingSubtypes.length > 1 && (
                <Row alignItems="center" gap="spacing.general.x4" justifyContent="space-between" minWidth={0}>
                  <Pressable onPress={() => toggleSubtype(subtype)} style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      color="text.secondary"
                      flex={1}
                      fontWeight="700"
                      minWidth={0}
                      textAlign="left"
                      variant="bodyBase"
                    >
                      {t(`subtype.${subtype}`)}
                    </Text>
                  </Pressable>

                  <Switch onValueChange={() => toggleSubtype(subtype)} value={selectedSubtypeSet.has(subtype)} />
                </Row>
              )}
              {!hideCategories &&
                ((subtype === ListingSubtype.object && !hasDonationSubtype) ||
                  (subtype === ListingSubtype.service && !simplifiedServices) ||
                  subtype === ListingSubtype.donation) &&
                (selectedSubtypeSet.has(subtype) ||
                  listingSubtypes.length === 1 ||
                  subtype === ListingSubtype.donation) && (
                  <CategoryFilter
                    closeModal={onSave}
                    isService={subtype === ListingSubtype.service}
                    value={subtype === ListingSubtype.service ? selectedServiceCategories : selectedCategories}
                  />
                )}
            </Column>
          ))}
        </Column>
      )}

      {!hideCategoryAndTagFilters && tagsEnabled ? (
        <Column gap={titleGap}>
          <Text fontWeight="700" variant="bodyBase">
            {t('titleTags')}
          </Text>

          <TagFilter closeModal={onSave} value={selectedTags} />
        </Column>
      ) : null}
    </Column>
  );
});

export { ListingsFiltersSheetContent };

function TransactionModeFilters({
  showTransactionModeFilters,
  titleGap,
  t,
  isSmallUI,
  toggleTransactionMode,
  selectedTransactionModes,
}: {
  showTransactionModeFilters: boolean;
  titleGap: ComponentProps<typeof Column>['gap'];
  t: TFunction<'listings-filters', undefined>;
  isSmallUI: boolean;
  toggleTransactionMode: (mode: TransactionMode) => void;
  selectedTransactionModes: TransactionMode[];
}): React.ReactNode {
  return showTransactionModeFilters ? (
    <Column gap={titleGap}>
      <Text fontWeight="700" variant="bodyBase">
        {t('titleType')}
      </Text>
      <Column
        gap="spacing.general.x4"
        style={{
          borderRadius: theme.borderRadii['radius.lg'],
          borderColor: theme.colors['surface.tertiary-neutral'],
          borderWidth: 1,
          padding: isSmallUI ? theme.spacing['spacing.general.x2'] : theme.spacing['spacing.general.x4'],
          width: '100%',
        }}
      >
        {[TransactionMode.lend, TransactionMode.give, TransactionMode.sell].map((mode) => (
          <Row key={mode} alignItems="center" gap="spacing.general.x4" justifyContent="space-between" minWidth={0}>
            <Pressable onPress={() => toggleTransactionMode(mode)} style={{ flex: 1, minWidth: 0 }}>
              <Text color="text.secondary" flex={1} fontWeight="700" minWidth={0} variant="bodyBase">
                {t(`transactionMode.${mode}`)}
              </Text>
            </Pressable>

            <Switch onValueChange={() => toggleTransactionMode(mode)} value={selectedTransactionModes.includes(mode)} />
          </Row>
        ))}
      </Column>
    </Column>
  ) : null;
}

function AudienceFilters({
  hideGroups,
  titleGap,
  t,
  isSmallUI,
  groups,
  addresses,
  toggleAddress,
  addressOrGroupIconSize,
  addressOrGroupTextProps,
  selectedAddresses,
  toggleGroup,
  selectedGroups,
}: {
  hideGroups: boolean;
  titleGap: ComponentProps<typeof Column>['gap'];
  t: TFunction<'listings-filters', undefined>;
  isSmallUI: boolean;
  groups: Group[];
  addresses: Address[];
  toggleAddress: (addressId: string) => void;
  addressOrGroupIconSize: number;
  addressOrGroupTextProps: Partial<ComponentProps<typeof Text>>;
  selectedAddresses: string[];
  toggleGroup: (groupId: string) => void;
  selectedGroups: string[];
}): React.ReactNode {
  const selectedAddressSet = useMemo(() => new Set(selectedAddresses), [selectedAddresses]);
  const selectedGroupSet = useMemo(() => new Set(selectedGroups), [selectedGroups]);

  return (
    !hideGroups && (
      <Column gap={titleGap} width="100%">
        <Text fontWeight="700" variant="bodyBase">
          {t('titleClubs')}
        </Text>

        <Column
          gap="spacing.general.x2"
          style={{
            borderRadius: theme.borderRadii['radius.lg'],
            backgroundColor: theme.colors['surface.tertiary-neutral'],
            padding: isSmallUI ? theme.spacing['spacing.general.x2'] : theme.spacing['spacing.general.x4'],
            width: '100%',
          }}
        >
          {(groups.length > 0 || addresses.length > 1) &&
            addresses.map((address) => (
              <Row
                key={address.id}
                alignItems="center"
                gap="spacing.general.x4"
                justifyContent="space-between"
                minWidth={0}
              >
                <Pressable onPress={() => toggleAddress(address.id)} style={{ flex: 1, minWidth: 0 }}>
                  <Row alignItems="center" flex={1} gap="spacing.general.x4" minWidth={0}>
                    {getAddressIcon(address, addressOrGroupIconSize)}

                    <Text {...addressOrGroupTextProps}>
                      {addresses.length === 1
                        ? t('neighborhoodClub', { ns: 'my-neighbourhood' })
                        : t('clubName', {
                            ns: 'my-neighbourhood',
                            clubName: address.name ?? t('neighbourhoodClubName', { ns: 'my-clubs' }),
                          })}
                    </Text>
                  </Row>
                </Pressable>

                <Switch
                  style={{ transform: [{ scale: 0.9 }] }}
                  onValueChange={() => toggleAddress(address.id)}
                  value={selectedAddressSet.has(address.id)}
                />
              </Row>
            ))}
          {groups.map((group) => (
            <Row
              key={group.id}
              alignItems="center"
              gap="spacing.general.x4"
              justifyContent="space-between"
              minWidth={0}
            >
              <Pressable onPress={() => toggleGroup(group.id)} style={{ flex: 1, minWidth: 0 }}>
                <Row alignItems="center" flex={1} gap="spacing.general.x4" minWidth={0}>
                  <Image
                    source={{ uri: group.picture }}
                    style={{
                      width: addressOrGroupIconSize,
                      height: addressOrGroupIconSize,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: theme.colors['surface.disabled'],
                    }}
                  />

                  <Text color="text.secondary" flex={1} fontWeight="700" minWidth={0} textAlign="left" fontSize={14}>
                    {`Club ${group.name}`}
                  </Text>
                </Row>
              </Pressable>

              <Switch
                style={{ transform: [{ scale: 0.9 }] }}
                onValueChange={() => toggleGroup(group.id)}
                value={selectedGroupSet.has(group.id)}
              />
            </Row>
          ))}
        </Column>
      </Column>
    )
  );
}

function OffersWithFeeFilter({
  isSmallUI,
  toggleOffersWithFee,
  hideOffersWithFee,
  t,
}: {
  isSmallUI: boolean;
  toggleOffersWithFee: () => void;
  hideOffersWithFee: boolean;
  t: TFunction<'listings-filters'>;
}) {
  return (
    <Column
      gap="spacing.general.x4"
      style={{
        borderRadius: theme.borderRadii['radius.lg'],
        borderColor: theme.colors['surface.tertiary-neutral'],
        borderWidth: 1,
        padding: isSmallUI ? theme.spacing['spacing.general.x2'] : theme.spacing['spacing.general.x4'],
        width: '100%',
      }}
    >
      <Row alignItems="center" gap="spacing.general.x4" justifyContent="space-between" minWidth={0}>
        <Pressable onPress={() => toggleOffersWithFee()} style={{ flex: 1, minWidth: 0 }}>
          <Text color="text.secondary" flex={1} fontWeight="700" minWidth={0} variant="bodyBase">
            {t('offersWithFeeLabel')}
          </Text>
        </Pressable>

        <Switch onValueChange={() => toggleOffersWithFee()} value={!hideOffersWithFee} />
      </Row>
    </Column>
  );
}
